import { formatDateStr, getPrefs } from "./prefs";

const grouped = new Intl.NumberFormat("ru-RU");
const plain = new Intl.NumberFormat("en-US", { useGrouping: false });

function fmtInt(n: number): string {
  return (getPrefs().groupSep ? grouped : plain).format(Math.round(n));
}

export function num(n: number | null | undefined): string {
  if (n === null || n === undefined || Number.isNaN(n)) return "—";
  return fmtInt(n);
}

export function money(n: number | null | undefined): string {
  if (n === null || n === undefined) return "—";
  const p = getPrefs();
  // Always the exact figure — no "млн"/"млрд" abbreviation (dropped 2026-09-11 per explicit
  // user request: "mln degan jadvalda kerak emas umuman" — money columns show the real number).
  if (p.moneyDecimals > 0) {
    const [int, frac = ""] = Math.abs(n).toFixed(p.moneyDecimals).split(".");
    const sign = n < 0 ? "-" : "";
    return `${sign}${fmtInt(Number(int))}${frac ? "," + frac : ""}`;
  }
  return fmtInt(n);
}

const MONTHS = ["янв", "фев", "мар", "апр", "мая", "июн", "июл", "авг", "сен", "окт", "ноя", "дек"];

/** "2026-09-09" -> per prefs.dateFmt (default "9 сен 2026" style kept for callers that want words) */
export function date(iso: string | null | undefined): string {
  if (!iso) return "—";
  return formatDateStr(iso);
}

/** always the word form "9 сен 2026" regardless of prefs (used where space is tight-but-labelled) */
export function dateWords(iso: string | null | undefined): string {
  if (!iso) return "—";
  const [y, m, d] = iso.slice(0, 10).split("-");
  return `${Number(d)} ${MONTHS[Number(m) - 1]} ${y}`;
}

/** "2026-09-09T17:30:00+05:00" -> "09.09.2026 17:30" */
export function datetime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const dt = new Date(iso);
  if (Number.isNaN(dt.getTime())) return iso;
  const p = (x: number) => String(x).padStart(2, "0");
  return `${p(dt.getDate())}.${p(dt.getMonth() + 1)}.${dt.getFullYear()} ${p(dt.getHours())}:${p(dt.getMinutes())}`;
}

/** "только что" / "40 мин назад" / "3 ч назад" / "2 дн назад" */
export function ago(iso: string | null | undefined, now = Date.now()): string {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "—";
  const min = Math.max(0, Math.round((now - t) / 60000));
  if (min < 1) return "только что";
  if (min < 60) return `${min} мин назад`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h} ч назад`;
  return `${Math.floor(h / 24)} дн назад`;
}

/** ETL runs every 2 h — data older than this is flagged as stale in the UI. */
export const STALE_AFTER_MS = 3 * 60 * 60 * 1000;
export function isStale(iso: string | null | undefined, now = Date.now()): boolean {
  if (!iso) return false;
  const t = new Date(iso).getTime();
  return !Number.isNaN(t) && now - t > STALE_AFTER_MS;
}

export function signed(n: number): string {
  return `${n > 0 ? "+" : ""}${num(n)}`;
}
